import cx from 'classnames'
import React, { InputHTMLAttributes } from 'react'
import { useFormContext } from 'react-hook-form'

import { TRACKING_MAPPING } from '@js/common/constants'

import Icon from '@f/elements/Icon'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { FormErrorMessage } from './ErrorMessage'

import './RadioInput.css'

type RadioOption = {
  value: string
  label: string
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { 'data-sel': string }
  icon?: string
  iconClassName?: string
}

export interface FormRadioGroupProps {
  name: string
  defaultValue?: string
  className?: string
  radioOptions: RadioOption[]
  radioTheme?: 'basic' | 'form'
  radioClassName?: string
  radioLabelClassName?: string
  radioActiveClassName?: string
  radioErrorMessageClassName?: string
  radioErrorFullWidth?: boolean
  isHorizontal?: boolean
  wtData?: $TSFixMe
  inputProps?: InputHTMLAttributes<HTMLInputElement>
}

export const RadioInput = ({
  name,
  radioTheme,
  wtData = {},
  radioErrorFullWidth,
  isHorizontal,
  radioOptions,
  className,
  radioLabelClassName,
  radioClassName,
  radioActiveClassName,
  radioErrorMessageClassName,
  ...props
}: FormRadioGroupProps) => {
  const { register, formState, getFieldState, watch } = useFormContext()
  const { isValidating } = formState
  const { error } = getFieldState(name, formState)
  const watchRadio = watch(name)

  const displayError = error?.message as string

  const handleOnClick = ({
    event,
    radio
  }: {
    event: React.MouseEvent<HTMLInputElement>
    radio: RadioOption & { wtData?: $TSFixMe }
  }) => {
    if (event && event.target) {
      const target = event.target as HTMLInputElement
      window._wt({
        category: 'radiobutton',
        action: 'click',
        values: [
          {
            type:
              TRACKING_MAPPING[name] && TRACKING_MAPPING[name].label
                ? TRACKING_MAPPING[name].label
                : name,
            value: target.value
          }
        ],
        ...(wtData['radiobutton'] ? wtData['radiobutton'] : {}),
        ...(radio.wtData && radio.wtData['radiobutton']
          ? radio.wtData['radiobutton']
          : {})
      })
    }
  }

  const radioGroupWrapper = (
    <>
      <div className={cx(className)}>
        {radioOptions.map(radio => {
          return (
            <label
              key={`radio_group_${name}_${radio.value}_${radio.label}`}
              className={cx(
                'RadioInput-wrapper',
                radioTheme && `RadioInput-wrapper-${radioTheme}`,
                isValidating && 'is-validating',
                displayError && 'f-c-bo-war',
                radioClassName,
                watchRadio === radio.value && radioActiveClassName
              )}
              {...props}>
              <input
                type='radio'
                className='RadioInput'
                value={radio.value}
                onClick={event => handleOnClick({ event, radio })}
                {...register(name)}
                {...radio.inputProps}
              />
              {
                <>
                  {radioTheme === 'basic' && (
                    <span
                      className={cx(
                        '[ f-m-v-010 ] RadioInput-dot',
                        displayError ? 'f-c-bo-war' : 'f-c-bo-neu050'
                      )}
                    />
                  )}
                  {radio.icon && (
                    <span className='[ f-d-bl f-t-centre ]'>
                      <Icon
                        icon={radio.icon}
                        className={cx(radio.iconClassName)}
                      />
                    </span>
                  )}
                  {radio.label && (
                    <span className={cx(radioLabelClassName)}>
                      {radio.label}
                    </span>
                  )}
                </>
              }
            </label>
          )
        })}
      </div>
      {displayError && (
        <FormErrorMessage
          className={cx(
            '[ f-d-bl f-m-b-020 f-c-fo-war ] FormError-wrapper',
            radioErrorFullWidth && 'FormError-wrapper--full-width',
            radioErrorMessageClassName
          )}>
          {displayError}
        </FormErrorMessage>
      )}
    </>
  )

  if (isHorizontal) {
    return <div className='[ f-pos-rel ]'>{radioGroupWrapper}</div>
  }

  return radioGroupWrapper
}
